import { useState, useEffect, useMemo } from "react";
import {
  Box, Tabs, Tab, Typography, Chip, Button, Stack,
  Card, CardContent, Divider, IconButton,
  useMediaQuery,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, CircularProgress
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HeightIcon from "@mui/icons-material/Height";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BlockIcon from "@mui/icons-material/Block";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import SportsMotorsportsIcon from "@mui/icons-material/SportsMotorsports";
import ExtensionIcon from "@mui/icons-material/Extension";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EditIcon from "@mui/icons-material/Edit";
import { ManagerService } from "../../client";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router";

import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  ListItemButton,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable'; // Icona per future
import HistoryIcon from '@mui/icons-material/History';           // Icona per passate
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'; // Icona per report
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'; // Icona per orari e chiusure
import SchedulePanel from "./schedule";
import { SmartphoneOutlined, Straighten } from "@mui/icons-material";


export const ASK_TALL_PLACEHOLDER = "(chiedere al cliente tramite il contatto telefonico)";

// Chiave localStorage dove salviamo il token del pannello manager, così la
// pagina /manager funziona anche senza token nel path (es. dopo che l'abbiamo
// ripulito dall'URL o in una visita successiva dallo stesso dispositivo).
const MANAGER_TOKEN_KEY = "manager_token";

// Il token della sezione "Orari e chiusure". Il link dall'admin usa il token
// delle prenotazioni quando il negozio ne ha (vale per entrambe le sezioni), ma
// un negozio senza prenotazioni o il team Outdoora arrivano con un token degli
// orari ("c12:firma", "s12:firma"): quello NON deve sostituire il token delle
// prenotazioni, quindi sta in una chiave a parte.
const SCHEDULE_TOKEN_KEY = "manager_schedule_token";
const isScheduleToken = (t) => /^[cs]\d+:/.test(t || "");


const drawerWidth = 240;

// Etichette leggibili delle due sezioni (future / passate). Usate per il
// titolo mostrato sopra le tab, così su mobile si capisce in che sezione
// ci troviamo anche quando la sidebar è chiusa.
const SECTION_LABELS = {
  future: "Prenotazioni Future",
  passate: "Prenotazioni Passate",
  report: "Report",
  orari: "Orari e chiusure",
};

// Messaggi "parlanti" per lo stato vuoto di ogni tab, così invece di un
// generico "Nessuna richiesta" l'utente capisce che ha gestito tutto.
function emptyMessageFor(status, section) {
  if (status === "pending") {
    return section === "passate"
      ? "Nessuna richiesta da approvare tra le prenotazioni passate."
      : "Hai approvato tutte le richieste. Nessuna richiesta da approvare.";
  }
  if (status === "approved") {
    return section === "passate"
      ? "Nessuna richiesta approvata tra le prenotazioni passate."
      : "Nessuna richiesta approvata al momento.";
  }
  // refused
  return section === "passate"
    ? "Nessuna richiesta cancellata tra le prenotazioni passate."
    : "Nessuna richiesta cancellata.";
}


// ─── Theme ───────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: { main: "#2E7D32" },
    background: { default: "#F2F2EF", paper: "#FFFFFF" },
    warning: { main: "#E6A817", light: "#FFF8E6", dark: "#7A5500" },
    error: { main: "#C32E2E", light: "#FFF0F0", dark: "#8B1A1A" },
    success: { main: "#2E7D32", light: "#EAF3DE", dark: "#27500A" },
  },
  typography: {
    fontFamily: "'Poppins', sans-serif",
    button: { textTransform: "none", fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          fontSize: 13,
          minHeight: 46,
          paddingInline: 16,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          border: "1px solid #E8E8E4",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 8,
          fontSize: 13,
        },
      },
    },
  },
});

// ─── Mock data ────────────────────────────────────────────────────────────────
// Date relative al momento del caricamento del modulo, così i casi (future > 24h,
// urgenti < 24h, passate) restano sempre coerenti indipendentemente da quando si testa.
const NOW = new Date();
const addH = (h) => new Date(NOW.getTime() + h * 3600_000);
const H = (h) => addH(h).toISOString();

const ALL_REQUESTS = [
  // ── Pending — future > 24h (modificabili) ─────────────────────────────────
  { id: 259, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'pending', tourDate: H(24 * 7 + 6), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }], participants: 2, contacts: { phone: '3517784499', name: 'Federico Marini' } },
  { id: 271, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'pending', tourDate: H(24 * 8 + 10), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: '168 cm' }, { id: 47, is_bike: false, name: 'Helmet' }], participants: 1, contacts: { phone: '3924587770', name: 'Stefano Galli' } },
  { id: 272, tour: 'Tour del Trasimeno - Pranzo incluso', status: 'pending', tourDate: H(24 * 9 + 4), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '162 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '190 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '158 cm' }, { id: 47, is_bike: false, name: 'Helmet' }], participants: 4, contacts: { phone: '3458877651', name: 'Anna Ferrari' } },

  // ── Pending — future < 24h (solo annullabili) ─────────────────────────────
  { id: 270, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'pending', tourDate: H(20), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: '172 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '185 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }], participants: 2, contacts: { phone: '3471129987', name: 'Chiara Esposito' } },
  { id: 273, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'pending', tourDate: H(23), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }], participants: 3, contacts: { phone: '3666220047', name: 'Roberto Greco' } },
  { id: 274, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'pending', tourDate: H(5), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: '170 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '177 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }, { id: -1, name: 'Extra Device', price: 5 }], participants: 2, contacts: { phone: '3489913321', name: 'Davide Moretti' } },

  // ── Pending — iniziata da poco (entro la tolleranza di EXPIRED_GRACE_HOURS:
  //    resta tra le future ed è ancora approvabile) ───────────────────────────
  { id: 281, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'pending', tourDate: H(-2), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: '175 cm' }, { id: 47, is_bike: false, name: 'Helmet' }], participants: 1, contacts: { phone: '3401178856', name: 'Paolo Serra' } },

  // ── Pending — passata (mai gestita in tempo) ──────────────────────────────
  { id: 279, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'pending', tourDate: H(-24 * 3), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }], participants: 2, contacts: { phone: '3477226613', name: 'Tommaso Barbieri' } },

  // ── Approved — future ─────────────────────────────────────────────────────
  { id: 267, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'approved', tourDate: H(24 * 5 + 3), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: '161 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '180 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }], participants: 2, contacts: { phone: '3487125566', name: 'Giulia Romano' } },
  { id: 250, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'approved', tourDate: H(24 * 34), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }], participants: 2, contacts: { phone: '3665598843', name: 'Elena Russo' } },
  { id: 275, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'approved', tourDate: H(16), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: '165 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '183 cm' }, { id: 47, is_bike: false, name: 'Helmet' }], participants: 2, contacts: { phone: '3327544488', name: 'Valentina Costa' } },
  { id: 276, tour: 'Tour delle colline di Cortona', status: 'approved', tourDate: H(24 * 30 + 8), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: '178 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '174 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '162 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '155 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '188 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }, { id: -1, name: 'Extra Device', price: 5 }], participants: 5, contacts: { phone: '3905512200', name: 'Mario Lombardi' } },
  { id: 280, tour: 'Tour del Trasimeno - Pranzo incluso', status: 'approved', tourDate: H(24 * 14 - 6), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: '171 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '165 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '180 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '140 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '155 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '182 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }, { id: -1, name: 'Extra Device', price: 5 }, { id: -1, name: 'Extra Device', price: 5 }], participants: 6, contacts: { phone: '3458890012', name: 'Caterina Ricci' } },

  // ── Approved — passate ────────────────────────────────────────────────────
  { id: 255, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'approved', tourDate: H(-24 * 21), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }], participants: 1, contacts: { phone: '3932644321', name: 'Marco Albanesi' } },
  { id: 251, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'approved', tourDate: H(-24), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }], participants: 2, contacts: { phone: '3394017782', name: 'Andrea Conti' } },
  { id: 278, tour: 'Tour del Trasimeno - Pranzo incluso', status: 'approved', tourDate: H(-24 * 2), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: '172 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '160 cm' }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: '180 cm' }, { id: 47, is_bike: false, name: 'Helmet' }], participants: 3, contacts: { phone: '3499876541', name: 'Giorgia Marchetti' } },

  // ── Refused — future > 24h ────────────────────────────────────────────────
  { id: 249, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'refused', tourDate: H(24 * 34 + 1), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }], participants: 2, contacts: { phone: '3278842210', name: 'Luca Bianchi' } },

  // ── Refused — future < 24h ────────────────────────────────────────────────
  { id: 264, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'refused', tourDate: H(15), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }, { id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }, { id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }, { id: 48, is_bike: true, name: 'E-bike', userTall: ASK_TALL_PLACEHOLDER }, { id: 47, is_bike: false, name: 'Helmet' }, { id: -1, name: 'Extra Device', price: 5 }], participants: 5, contacts: { phone: '3802269013', name: 'Sara De Luca' } },

  // ── Refused — passata ─────────────────────────────────────────────────────
  { id: 277, tour: 'Cinque Terre Ebike tour & Rientro in treno', status: 'refused', tourDate: H(-24 * 4), items: [{ id: 48, is_bike: true, name: 'E-bike', userTall: '169 cm' }, { id: 47, is_bike: false, name: 'Helmet' }], participants: 1, contacts: { phone: '3382214489', name: 'Lorenzo Vitale' } },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function hoursUntil(date) {
  let now = new Date()
  return (date - now) / 3_600_000;
}

function fmtDate(date) {
  return date.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(date) {
  return date.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }) + " — " + date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

// Genera il token b64 usato dagli endpoint /api/rental_request e
// /api/rental_cancel (lo stesso formato di Payment._get_approval_id lato
// Django: "{payment_id}__{timestamp}"). Un'unica funzione così cancel e
// approve condividono lo stesso schema di token.
function buildBookingToken(request) {
  return btoa(`${request.id}__${new Date().getTime()}`);
}

function buildRentalCancelUrl(request) {
  return `/api/rental_cancel/?data=${buildBookingToken(request)}`;
}

function buildRentalApproveUrl(request) {
  return `/api/rental_request/${buildBookingToken(request)}/?status=approved`;
}

// Estrae solo le cifre dal numero (richiesto da `wa.me/<num>`).
// Non aggiungiamo prefissi di paese: il numero potrebbe essere di qualsiasi
// nazionalità e prendere assunzioni sbagliate (es. forzare +39) era un bug.
function phoneDigitsOnly(phone) {
  return String(phone || "").replace(/\D/g, "");
}

const STATUS_META = {
  pending:  { label: "Da approvare", color: "warning" },
  approved: { label: "Approvata",    color: "success" },
  refused:  { label: "Rifiutata",    color: "error"   },
};

function iconForItem(item) {
  // Le bici (is_bike) hanno SEMPRE l'icona bici, anche se il nome contiene
  // "helmet" (es. "E-Bike full suspension + helmet").
  if (isBikeItem(item)) return <DirectionsBikeIcon fontSize="small" />;
  const n = (item?.name || "").toLowerCase();
  if (item?.id === -1 || n.includes("device")) return <SmartphoneOutlined fontSize="small" />;
  if (n.includes("helmet") || n.includes("casco")) return <SportsMotorsportsIcon fontSize="small" />;
  if (n.includes("bike") || n.includes("bici")) return <DirectionsBikeIcon fontSize="small" />;
  return <ExtensionIcon fontSize="small" />;
}

function nameForItem(item) {
  const name = item?.name;
  const n = (name || "").toLowerCase();
  // Extra device: etichetta "Porta telefono" (riconosciuto per id -1 o nome).
  if (item?.id === -1 || n.includes("device")) return "Porta telefono";
  // Le bici mantengono il NOME REALE anche se contiene "helmet"
  // (es. "E-Bike full suspension + helmet"): NON vanno rinominate "Casco".
  // Solo i caschi veri e propri (non bici) vengono etichettati "Casco".
  if (!isBikeItem(item) && (n.includes("helmet") || n.includes("casco"))) return "Casco";
  return name;
}

function qryForItem(id, qty) {
  if (id === -1) return qty + 1;
  return qty
}

function aggregateItems(items) {
  // Raggruppa gli items per id (con fallback su name+price) così che gli oggetti
  // dello stesso tipo siano in un'unica riga con il conteggio totale.
  // Le altezze (userTall) vengono accumulate per essere mostrate sotto.
  const map = new Map();
  for (const it of items || []) {
    const key = `${it.id ?? "_"}__${it.name}__${it.price ?? ""}`;
    const prev = map.get(key);
    if (prev) {
      prev.qty += 1;
      if (it.userTall) prev.userTalls.push(it.userTall);
    } else {
      map.set(key, {
        ...it,
        qty: 1,
        userTalls: it.userTall ? [it.userTall] : [],
      });
    }
  }
  return Array.from(map.values());
}

// Ore di TOLLERANZA dopo l'orario di inizio durante le quali una prenotazione
// resta nella sezione "future" (e quindi ancora gestibile: card "Gestisci",
// pulsanti Accetta/Rifiuta, alert altezze mancanti). Serve a dare flessibilità
// al negoziante nell'approvazione e a coprire i ritardi del cliente: solo
// scadute queste ore il tour passa nelle "Prenotazioni Passate".
const EXPIRED_GRACE_HOURS = 4;

function isExpired(date) {
  let now = new Date()
    const diff = date - now;
    return diff <= -EXPIRED_GRACE_HOURS * 3_600_000
}

// Normalizza un valore di altezza per la modale: estrae solo i cm da
// stringhe tipo "162 cm" / "162" / 162 / "  162cm". Gli altri formati
// (es. "5'7\"") vengono lasciati intatti così l'utente può eventualmente
// correggerli. Vuoto/placeholder → "" (campo da compilare).
function normalizeHeight(raw) {
  if (raw === null || raw === undefined) return "";
  const s = String(raw).trim();
  if (!s) return "";
  if (s === ASK_TALL_PLACEHOLDER) return "";
  const m = s.match(/^(\d{2,3})\s*(cm)?$/i);
  if (m) return m[1];
  return s;
}

// Un item è una "bike" in base al campo `is_bike` calcolato dal server
// (RentalObjects.is_bike, fonte autorevole). Per retrocompatibilità con dati
// mock o risposte vecchie privi di `is_bike`, si ricade sull'euristica storica
// (userTall valorizzato oppure 'bike' nel name).
function isBikeItem(item) {
  if (!item) return false;
  if (typeof item.is_bike === "boolean") return item.is_bike;
  if (item.userTall !== undefined && item.userTall !== null && item.userTall !== "") return true;
  if (typeof item.name === "string" && item.name.toLowerCase().indexOf("bike") !== -1) return true;
  return false;
}

// Una bike è "senza altezza" se userTall è vuoto/placeholder.
function isMissingHeight(item) {
  if (!isBikeItem(item)) return false;
  return normalizeHeight(item.userTall) === "";
}

// Ritorna gli indici (sull'array originale chart_objects.items)
// di tutte le bici, e quante mancano di altezza.
function bikesInfo(items) {
  const bikeIndexes = [];
  let missing = 0;
  (items || []).forEach((it, idx) => {
    if (isBikeItem(it)) {
      bikeIndexes.push(idx);
      if (isMissingHeight(it)) missing++;
    }
  });
  return { bikeIndexes, missingCount: missing, totalBikes: bikeIndexes.length };
}

// Replica lato React di customer/email/utils.py::generate_whatsapp_link.
// Mantieni i due testi in sync: la mail e i pulsanti del pannello devono
// inviare ESATTAMENTE lo stesso messaggio precompilato al cliente, nella
// sua lingua (default en).
function generateWhatsappLink({ phoneNumber, tourName, bikeCount, language = "en" }) {
  if (!phoneNumber) return null;
  try {
    const lang = (language || "en").toLowerCase();
    const messages = {
      it: (
        `Salve! Per la sua prenotazione del tour '${tourName}' ci risulta che vi servono ${bikeCount} bici. ` +
        `Ci potete scrivere le altezze di ciascun partecipante? È necessario per potervi riservare le e-bike. \nGrazie!`
      ),
      en: (
        `Hello! Regarding your booking for the '${tourName}', we have noted that you need ${bikeCount} bikes. ` +
        `Could you please send us the heights of each participant? This is necessary for us to reserve the correct e-bikes for you. \nThanks!`
      ),
    };
    const text = messages[lang] || messages.en;
    const cleanNumber = String(phoneNumber).replace(/\D/g, "");
    return `https://wa.me/${cleanNumber}?text=${encodeURIComponent(text)}`;
  } catch (e) {
    return null;
  }
}

function useCountdown(date) {
  const calc = () => {
    let now = new Date()
    const diff = date - now;
    if (diff <= -EXPIRED_GRACE_HOURS * 3_600_000) return { label: "Concluso", color: "default", isExpired: true };
    if (diff <= 0) {
      // Tour iniziato da meno di EXPIRED_GRACE_HOURS ore: resta tra le future
      // ed è ancora gestibile; mostriamo da quanto è iniziato, non "Concluso".
      const startedMin = Math.floor(-diff / 60_000);
      const h = Math.floor(startedMin / 60);
      const m = startedMin % 60;
      return {
        label: h > 0 ? `Iniziato da ${h}h ${String(m).padStart(2, "0")}min` : `Iniziato da ${m}min`,
        color: "error",
        isExpired: false,
      };
    }
    const totalSec = Math.floor(diff / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h < 24) {
      return {
        label: `${h}h ${String(m).padStart(2, "0")}min ${String(s).padStart(2, "0")}s`,
        color: h < 6 ? "error" : "warning",
        isExpired: false,
      };
    }
    const days = Math.floor(h / 24);
    return {
      label: `tra ${days} ${days === 1 ? "giorno" : "giorni"}`,
      color: "success",
      isExpired: false,
    };
  };
  const [val, setVal] = useState(calc);
  useEffect(() => {
    const iv = setInterval(() => setVal(calc()), 1000);
    return () => clearInterval(iv);
  }, [date]);
  return val;
}

// ─── SubGroup label config ────────────────────────────────────────────────────
const SUBGROUPS = [
  // {
  //   key: "height",
  //   label: "Chiedi altezze al cliente",
  //   dotColor: "#E6A817",
  //   filter: (r) => r.needsHeight,
  // },
  {
    key: "modifiable",
    label: "Data tour > 24h — modificabili",
    dotColor: "#2E7D32",
    // filter: (r) => !r.needsHeight && hoursUntil(r.tourDate) > 24,
    filter: (r) => hoursUntil(r.tourDate) > 24,
  },
  {
    key: "urgent",
    label: "Data tour < 24h — solo annullabili",
    dotColor: "#C32E2E",
    // filter: (r) => !r.needsHeight && hoursUntil(r.tourDate) <= 24,
    filter: (r) => hoursUntil(r.tourDate) <= 24,
  },
];

// ─── Single request card ──────────────────────────────────────────────────────
function RequestCard({ request, isRefused, isUrgent, onView }) {
  const cd = useCountdown(request.tourDate);
  const expired = isExpired(request.tourDate);
  const { missingCount } = bikesInfo(request.items);
  // Mostriamo l'alert "altezze mancanti" solo finché la prenotazione è
  // davvero gestibile (non rifiutata e non scaduta). Sulle approvate ha
  // ancora senso vederlo perché il negoziante può comunque aggiornarle.
  const showMissingHeightsAlert = missingCount > 0 && !isRefused && !expired;

  return (
    <Card sx={{ borderRadius: 2, mb: 1 }}>
      <CardContent sx={{ p: "12px 14px !important" }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          {/* Info */}
          <Box flex={1} minWidth={0}>
            <Typography
              fontSize={14}
              fontWeight={600}
              noWrap
              color="text.primary"
              mb={0.4}
            >
              {request.tour}
            </Typography>
            <Stack direction="row" gap={1.5} flexWrap="wrap">
              <Stack direction="row" alignItems="center" gap={0.4}>
                <CalendarTodayIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                <Typography fontSize={12} color="text.secondary">
                  {fmtDate(request.tourDate)}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={0.4}>
                <PeopleOutlineIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                <Typography fontSize={12} color="text.secondary">
                  {request.participants} partecipant{request.participants === 1 ? "e" : "i"}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" gap={0.8} flexWrap="wrap" mt={0.8}>
              {/* Countdown pill — nascosta per richieste rifiutate o concluse */}
              {!isUrgent && !isRefused && !expired && (
                <Chip
                  icon={<AccessTimeIcon style={{ fontSize: 11 }} />}
                  label={cd.label}
                  color={cd.color}
                  size="small"
                  sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                />
              )}
              {isUrgent && !isRefused && !expired && (
                <Chip
                  icon={<AccessTimeIcon style={{ fontSize: 11 }} />}
                  label={cd.label}
                  color="error"
                  size="small"
                  sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                />
              )}
              {/* Alert altezze mancanti */}
              {showMissingHeightsAlert && (
                <Chip
                  icon={<Straighten style={{ fontSize: 12, transform: "rotate(-45deg)" }} />}
                  label={`Altezze mancanti${missingCount > 1 ? ` (${missingCount})` : ""}`}
                  color="warning"
                  size="small"
                  sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                />
              )}
            </Stack>
          </Box>

          {/* CTA: il pulsante "Rifiuta" è stato spostato nel dettaglio */}
          <Button
            variant="outlined"
            color={isRefused ? "inherit" : "primary"}
            size="small"
            sx={isRefused
              ? { flexShrink: 0, color: "text.secondary", borderColor: "divider" }
              : { flexShrink: 0 }}
            onClick={() => onView && onView(request)}
          >
            {request.status === "pending" && !expired ? "Gestisci" : "Visualizza"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─── Modale altezze ───────────────────────────────────────────────────────────
// Permette al negoziante di inserire le altezze dei partecipanti dopo averle
// raccolte via WhatsApp/telefono. Pre-compila i valori già noti. Salva tramite
// `ManagerService.updateHeights` e invoca onSaved con il booking aggiornato
// così il parent può rinfrescare la lista in-place.
function HeightsDialog({ open, request, token, onClose, onSaved }) {
  // Ricalcola i valori iniziali a partire dagli items della prenotazione.
  // Le altezze già presenti vengono SEMPRE precompilate (anche in caso di
  // modifica di valori già inseriti). I valori vengono normalizzati per
  // estrarre i soli cm da formati tipo "162 cm" / 162 / "162"; il placeholder
  // "(chiedere al cliente...)" è equivalente a vuoto.
  const initial = useMemo(() => {
    const { bikeIndexes } = bikesInfo(request?.items || []);
    return bikeIndexes.map((idx) => normalizeHeight(request.items[idx].userTall));
  }, [request]);

  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Re-sync con `initial` ad ogni cambio dell'oggetto request (salvataggio,
  // refetch) E ad ogni riapertura della modale, così l'utente vede sempre i
  // valori correnti precompilati.
  useEffect(() => {
    setValues(initial);
    setError(null);
  }, [initial, open]);

  if (!request) return null;

  const phone = request.contacts?.phone;
  // Stesso testo precompilato della mail (vedi backend `generate_whatsapp_link`)
  // nella lingua del cliente. Il numero di bici lo calcoliamo da chart_objects.items.
  const { totalBikes } = bikesInfo(request.items);
  const waUrl = generateWhatsappLink({
    phoneNumber: phone || null,
    tourName: request.tour,
    bikeCount: totalBikes || request.participants || 1,
    language: request.locale,
  });

  // Salva consentito se tutti i campi hanno almeno un valore non vuoto.
  // Non forziamo il parsing a numero: tolleriamo eventuali formati legacy
  // (es. piedi/pollici) lasciando il valore così com'è.
  const canSave = values.length > 0 && values.every((v) => String(v || "").trim() !== "");

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await ManagerService.updateHeights(token, request.id, values);
      if (updated && updated.error) {
        setError(updated.error);
      } else if (updated) {
        onSaved && onSaved({ ...updated, tourDate: new Date(updated.tourDate) });
        onClose();
      }
    } catch (e) {
      setError("Errore nel salvataggio. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>Altezze partecipanti</DialogTitle>
      <DialogContent>
        <Typography fontSize={13} color="text.secondary" mb={2}>
          Inserisci le altezze in cm dei partecipanti per preparare le bici
          giuste. Se non le hai ancora, contatta il cliente su WhatsApp o per
          telefono e riapri questa finestra quando le hai.
        </Typography>

        {phone && (
          <Stack direction={{ xs: "column", sm: "row" }} gap={1} mb={2}>
            {waUrl && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#25D366",
                  borderColor: "#25D366",
                  "&:hover": { borderColor: "#1ebe5d", backgroundColor: "rgba(37,211,102,0.06)" },
                }}
              >
                WhatsApp
              </Button>
            )}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PhoneIcon />}
              href={`tel:${phone}`}
            >
              Chiama {phone}
            </Button>
          </Stack>
        )}

        <Stack gap={1.5}>
          {values.map((v, i) => (
            <TextField
              key={i}
              label={`Altezza partecipante #${i + 1} (cm)`}
              size="small"
              type="text"
              inputMode="numeric"
              value={v}
              onChange={(e) => {
                const next = [...values];
                next[i] = e.target.value;
                setValues(next);
              }}
              fullWidth
            />
          ))}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Annulla
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!canSave || saving}
        >
          {saving ? "Salvataggio…" : "Salva altezze"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Request detail page ──────────────────────────────────────────────────────
function RequestDetail({ request, onBack, token, onRequestUpdated }) {
  const cd = useCountdown(request.tourDate);
  const statusMeta = STATUS_META[request.status] || { label: request.status, color: "default" };
  const aggregated = aggregateItems(request.items);
  const isRefused = request.status === "refused";
  const isApproved = request.status === "approved";
  const expired = isExpired(request.tourDate);
  // I pulsanti Accetta/Rifiuta hanno senso solo per richieste pending future
  const showActions = request.status === "pending" && !expired;

  const { totalBikes, missingCount } = bikesInfo(request.items);
  // Basta anche UNA sola altezza mancante per attivare la modale; non
  // vincoliamo a showActions così è possibile compilare/correggere altezze
  // anche su prenotazioni già approvate.
  const needsHeights = totalBikes > 0 && missingCount > 0;
  // Apertura automatica della modale altezze quando arrivo sul dettaglio
  // di una prenotazione che ne ha ancora bisogno. Lo facciamo una volta
  // sola per id, così se l'utente la chiude esplicitamente non riappare
  // ad ogni re-render del parent.
  // NB: solo per prenotazioni ancora in attesa di approvazione. Su quelle
  // già approvate o cancellate l'apertura automatica sarebbe invasiva — il
  // negoziante può comunque aprire la modale a mano dal pulsante
  // "Modifica altezze" per correggere/integrare i dati.
  const shouldAutoOpenHeights = needsHeights && request.status === "pending";
  const [heightsOpen, setHeightsOpen] = useState(false);
  const [autoOpenedFor, setAutoOpenedFor] = useState(null);
  useEffect(() => {
    if (shouldAutoOpenHeights && autoOpenedFor !== request.id) {
      setHeightsOpen(true);
      setAutoOpenedFor(request.id);
    }
  }, [shouldAutoOpenHeights, request.id, autoOpenedFor]);

  // Modale di conferma per l'accettazione: l'azione è irreversibile (parte
  // la mail di conferma al cliente + creazione QR), quindi chiediamo un
  // doppio check al negoziante prima di andare all'endpoint che la processa.
  const [confirmAcceptOpen, setConfirmAcceptOpen] = useState(false);

  const handleRefuse = () => {
    window.location.href = buildRentalCancelUrl(request);
  };

  const handleAccept = () => {
    setConfirmAcceptOpen(true);
  };

  const confirmAccept = () => {
    window.location.href = buildRentalApproveUrl(request);
  };

  return (
    <Box>
      {/* Header sticky con back e tour */}
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid #E8E8E4",
          px: 2,
          py: 1.5,
          position: "sticky",
          top: 0,
          zIndex: 5,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Button
            size="large"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ color: "text.primary", fontSize: 20 }}
          >
            Vedi tutte le prenotazioni
          </Button>
          <Box flex={1} />
          <Chip
            label={statusMeta.label}
            color={statusMeta.color}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Stack>
      </Box>

      <Box p={2}>
        {/* Nome del negozio: mostrato sopra il titolo del tour così il gestore
            (che può avere più negozi con la stessa email) capisce subito a
            quale negozio si riferisce la prenotazione. */}
        {request.shop && (
          <Typography
            fontSize={14}
            fontWeight={700}
            color="primary.main"
            sx={{ textTransform: "uppercase", letterSpacing: 0.3 }}
            mb={0.5}
          >
            {request.shop}
          </Typography>
        )}
        {/* Tour name */}
        <Typography fontSize={22} fontWeight={700} color="text.primary" mb={0.5}>
          {request.tour}
        </Typography>
        <Typography fontSize={13} color="text.secondary" mb={2}>
          Richiesta #{request.id}
        </Typography>

        {/* Info principali */}
        <Card sx={{ borderRadius: 2, mb: 2 }}>
          <CardContent>
            <Stack gap={1.2}>
              <Stack direction="row" alignItems="center" gap={1}>
                <CalendarTodayIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography fontSize={14} color="text.primary">
                  {fmtDateTime(request.tourDate)}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <PeopleOutlineIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography fontSize={14} color="text.primary">
                  {request.participants} partecipant{request.participants === 1 ? "e" : "i"}
                </Typography>
              </Stack>
              {!isRefused && !expired && (
                <Stack direction="row" alignItems="center" gap={1}>
                  <AccessTimeIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Chip
                    label={cd.label}
                    color={cd.color}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Contatto cliente */}
        {request.contacts && (
          <>
            <Typography fontSize={16} fontWeight={700} mb={1}>
              Contatto cliente
            </Typography>
            <Card sx={{ borderRadius: 2, mb: 2 }}>
              <CardContent>
                <Stack gap={1.2}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <PersonOutlineIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography fontSize={14} fontWeight={600} color="text.primary">
                      {request.contacts.name || "—"}
                    </Typography>
                  </Stack>
                  {request.contacts.phone && (
                    <>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <PhoneIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                        <Typography
                          fontSize={14}
                          color="text.primary"
                          component="a"
                          href={`tel:${request.contacts.phone}`}
                          sx={{ textDecoration: "none", color: "inherit" }}
                        >
                          {request.contacts.phone}
                        </Typography>
                      </Stack>
                      <Stack direction={{ xs: "column", sm: "row" }} gap={1} mt={0.5}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PhoneIcon />}
                          href={`tel:${request.contacts.phone}`}
                          sx={{ flex: 1 }}
                        >
                          Chiama
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<WhatsAppIcon />}
                          href={`https://wa.me/${phoneDigitsOnly(request.contacts.phone)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            flex: 1,
                            color: "#25D366",
                            borderColor: "#25D366",
                            "&:hover": { borderColor: "#1ebe5d", backgroundColor: "rgba(37,211,102,0.06)" },
                          }}
                        >
                          WhatsApp
                        </Button>
                      </Stack>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </>
        )}

        <Alert severity={needsHeights ? "warning": "success"} sx={{ mb: 4, borderRadius: 2, flex:1,
           '& .MuiAlert-message': {
            flex: 1
           }
         }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flex={1}>
            {needsHeights ? (
              <div>
                Mancano le altezze di {missingCount} partecipante{missingCount === 1 ? "" : "i"}.
                Contatta il cliente su WhatsApp e inseriscile prima di approvare.
              </div>
            ): (
              <div>
                Tutte le altezze sono state acquisite
              </div>
            )}
            {totalBikes > 0 && (
              <Button
                size="normal"
                sx={{minWidth:"110px"}}
                variant={missingCount > 0 ? "contained" : "outlined"}
                color={missingCount > 0 ? "warning" : "primary"}
                startIcon={missingCount > 0 ? <Straighten sx={{transform: "rotate(-45deg)"}}/> : <EditIcon />}
                onClick={() => setHeightsOpen(true)}
              >
                {missingCount > 0 ? `Inserisci altezze` : "Modifica altezze"}
              </Button>
            )}
          </Stack>
        </Alert>
          
        {/* Attrezzatura richiesta */}
        <Typography fontSize={16} fontWeight={700} marginLeft={"10px"}>
          Attrezzatura richiesta
        </Typography>
        <br/>
        
        <Card sx={{ borderRadius: 2, mb: 2 }}>
          <CardContent>
            {aggregated.length === 0 ? (
              <Typography fontSize={13} color="text.secondary">
                Nessun oggetto selezionato.
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />} gap={1.2}>
                {aggregated.map((it, idx) => (
                  <ChartObjectItem it={it} key={`${it.name}-${idx}`}/>
                ))}
                {!aggregated.find(it => it.id === -1) &&
                  // se non c'è extra device lo aggiungo artificiale perchè 1 porta telefono lo devono mettere
                  // la qty è 0 perchè il componente la aumenta i porta telefoni di 1
                  <ChartObjectItem it={{
                    id: -1,
                    name: "Extra Device",
                    qty: 0 
                  }} />
                }
              </Stack>
            )}

          </CardContent>
        </Card>

        {/* Azioni - solo per richieste in attesa di approvazione */}
        {showActions && (
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.2} mt={1}>
            <Button
              variant="outlined"
              color="error"
              size="large"
              startIcon={<CancelOutlinedIcon />}
              onClick={handleRefuse}
              sx={{ flex: 1 }}
            >
              Rifiuta
            </Button>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={handleAccept}
              sx={{ flex: 1 }}
            >
              Accetta
            </Button>
          </Stack>
        )}
        {isRefused && (
          <Box mt={1}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ color: "text.secondary" }}>
              <BlockIcon sx={{ fontSize: 18 }} />
              <Typography fontSize={13}>
                Questa richiesta è stata rifiutata.
              </Typography>
            </Stack>
            {request.cancel_reason && (
              <Card sx={{ borderRadius: 2, mt: 1.5, borderColor: "error.light", borderWidth: 1, borderStyle: "solid" }}>
                <CardContent>
                  <Typography fontSize={13} fontWeight={700} color="error.dark" mb={0.5}>
                    Motivo del rifiuto
                  </Typography>
                  <Typography fontSize={13} color="text.primary" sx={{ whiteSpace: "pre-wrap" }}>
                    {request.cancel_reason}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
        {isApproved && (
          <Box mt={1}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ color: "success.dark" }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
              <Typography fontSize={13}>
                Questa richiesta è stata approvata.
              </Typography>
            </Stack>
          </Box>
        )}
        {expired && request.status === "pending" && (
          <Box mt={1}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ color: "text.secondary" }}>
              <AccessTimeIcon sx={{ fontSize: 18 }} />
              <Typography fontSize={13}>
                Questa prenotazione è passata e non è più gestibile.
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>

      <HeightsDialog
        open={heightsOpen}
        request={request}
        token={token}
        onClose={() => setHeightsOpen(false)}
        onSaved={(updated) => onRequestUpdated && onRequestUpdated(updated)}
      />

      <Dialog
        open={confirmAcceptOpen}
        onClose={() => setConfirmAcceptOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1 }}>Confermi l'accettazione?</DialogTitle>
        <DialogContent>
          <Typography fontSize={14} color="text.secondary">
            Stai per accettare la prenotazione <b>#{request.id}</b> di{" "}
            <b>{request.contacts?.name || "—"}</b> per il tour{" "}
            <b>{request.tour}</b>.
          </Typography>
          {needsHeights && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Attenzione: mancano ancora le altezze di {missingCount}{" "}
              partecipante{missingCount === 1 ? "" : "i"}. Puoi inserirle
              dal pulsante "Inserisci altezze" prima di accettare.
            </Alert>
          )}
          <Typography fontSize={13} color="text.secondary" mt={2}>
            Procedendo verrà inviata al cliente la conferma di prenotazione
            con il link di attivazione dell'app. L'azione non è reversibile.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmAcceptOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={confirmAccept}
          >
            Sì, accetta
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Subgroup section ─────────────────────────────────────────────────────────
function SubGroupSection({ sg, requests, isRefused, onView, hideHeader, index }) {
  const items = requests
    .filter(sg.filter)
    .sort((a, b) => a.tourDate - b.tourDate);

  if (!items.length) return null;

  return (
    <Box mb={2.5}>
      {/* Label row */}
      {!hideHeader && (
        <Stack direction="row" alignItems="center" gap={1} mb={1} mt={index > 0 ? 5: 0}>
          <Box
            sx={{
              width: 8, height: 8, borderRadius: "50%",
              backgroundColor: sg.dotColor, flexShrink: 0,
            }}
          />
          <Typography fontSize={16} fontWeight={600} letterSpacing="0.04em">
            {sg.label}
          </Typography>
        </Stack>
      )}

      {items.map((r) => (
        <RequestCard
          key={r.id}
          request={r}
          isRefused={isRefused}
          isUrgent={sg.key === "urgent"}
          onView={onView}
        />
      ))}
    </Box>
  );
}

function ChartObjectItem({it}) {
  return (
    <Stack direction="row" alignItems="flex-start" gap={1.2}>
      <Box sx={{ color: "text.secondary", mt: 0.3 }}>
        {iconForItem(it)}
      </Box>
      <Box flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography fontSize={14} fontWeight={600} color="text.primary">
            {nameForItem(it)}
          </Typography>
          <Chip
            label={`× ${qryForItem(it.id, it.qty)}`}
            size="small"
            sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
          />
        </Stack>
        {it.userTalls && it.userTalls.length > 0 && (
          <Stack gap={0.3} mt={0.5}>
            {it.userTalls.map((h, hIdx) => (
              <Stack
                key={hIdx}
                direction="row"
                alignItems="center"
                gap={0.5}
              >
                <Straighten sx={{ fontSize: 14, color: "warning.dark", transform: "rotate(-45deg)" }} />
                <Typography fontSize={12} color="warning.dark">
                  Altezza utente {it.userTalls.length > 1 ? `#${hIdx + 1}` : ""}: {h}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  )
}

// ─── Category tab panel ───────────────────────────────────────────────────────
function CategoryPanel({ status, allRequests, onView, section }) {

  const isRefused = status === "refused";
  const requests = allRequests.filter((r) => r.status === status);
  const hasAny = SUBGROUPS.some((sg) => requests.filter(sg.filter).length > 0);

  // La distinzione modificabili / solo annullabili (> 24h vs < 24h) ha senso
  // solo per le richieste ancora da approvare nella sezione future.
  // Per approvate, canellate e prenotazioni passate non mostriamo le label
  // dei sottogruppi.
  const showSubgroupHeaders = section === "future" && status === "pending";

  // Nel tab "Approvate" mostriamo prima quelle più vicine alla scadenza
  // (sottogruppo "urgent" = chip rossa) e poi quelle più lontane ("modifiable" = verdi).
  const orderedSubgroups = status === "approved"
    ? [...SUBGROUPS].sort((a, b) => (a.key === "urgent" ? -1 : b.key === "urgent" ? 1 : 0))
    : SUBGROUPS;

  if (!hasAny) {
    return (
      <Box py={4} textAlign="center">
        <Typography fontSize={14} color="text.secondary">
          {emptyMessageFor(status, section)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box pt={2}>
      {orderedSubgroups.map((sg, index) => (
        <SubGroupSection
          key={sg.key}
          sg={sg}
          index={index}
          requests={requests}
          isRefused={isRefused}
          onView={onView}
          hideHeader={!showSubgroupHeaders}
        />
      ))}
    </Box>
  );
}

// ─── Tab badge (count) ────────────────────────────────────────────────────────
function TabLabel({ label, status, allRequests }) {
  const count = allRequests.filter((r) => r.status === status).length;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <Stack direction="row" alignItems="center" gap={0.8} style={{
      fontSize: isMobile? '14px': '16px', padding: 0,
    }}>
      {label}
      <Box
        sx={{
          minWidth: 18, height: 18, borderRadius: "9px",
          backgroundColor: status === "pending" ? "primary.main" : "action.selected",
          color: status === "pending" ? "#fff" : "text.secondary",
          fontSize: 10, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center",
          px: 0.5,
          padding: 0
        }}
      >
        {count}
      </Box>
    </Stack>
  );
}

// ─── Root component ───────────────────────────────────────────────────────────
// ─── Report ───────────────────────────────────────────────────────────────────
// Formatta una data come gg/mm/aaaa (es. 01/07/2026).
function fmtDayMonthYear(date) {
  return date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// Nome del mese + anno con iniziale maiuscola (es. "Luglio 2026").
function monthLabel(date) {
  const s = date.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Calcola i numeri del report per l'intervallo [start, end] (estremi inclusi),
// differenziando prenotazioni approvate e ancora da approvare. Le prenotazioni
// cancellate (refused) sono SEMPRE escluse. Per il mese corrente `end` è
// "adesso", così contiamo solo i tour già svolti = bici effettivamente
// consegnate fino ad oggi. Le bici sono contate con bikesInfo (campo is_bike).
function computePeriodStats(requests, start, end) {
  const inRange = (requests || []).filter(
    (r) =>
      r.status !== "refused" &&
      r.tourDate instanceof Date &&
      r.tourDate >= start &&
      r.tourDate <= end
  );
  const approved = inRange.filter((r) => r.status === "approved");
  const pending = inRange.filter((r) => r.status === "pending");
  const bikesOf = (list) =>
    list.reduce((sum, r) => sum + bikesInfo(r.items).totalBikes, 0);
  return {
    bookingsApproved: approved.length,
    bookingsPending: pending.length,
    bikesApproved: bikesOf(approved),
    bikesPending: bikesOf(pending),
  };
}

// Singolo valore (numero grande + etichetta) usato nelle righe del report.
function ReportStatPill({ value, label, color }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 130,
        backgroundColor: "background.default",
        borderRadius: 2,
        px: 2,
        py: 1.5,
      }}
    >
      <Typography fontSize={30} fontWeight={800} lineHeight={1.1} color={color}>
        {value}
      </Typography>
      <Typography
        fontSize={12}
        fontWeight={600}
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.3 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// Blocco di una metrica (Prenotazioni / Bici) con i due valori differenziati.
function ReportMetricBlock({ icon, title, approvedValue, pendingValue }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} mb={1}>
        {icon}
        <Typography fontSize={15} fontWeight={700} color="text.primary">
          {title}
        </Typography>
        <Box flex={1} />
        <Typography fontSize={13} color="text.secondary">
          Totale: {approvedValue + pendingValue}
        </Typography>
      </Stack>
      <Stack direction="row" gap={1.2} flexWrap="wrap">
        <ReportStatPill value={approvedValue} label="Approvate" color="success.main" />
        <ReportStatPill value={pendingValue} label="Da approvare" color="warning.dark" />
      </Stack>
    </Box>
  );
}

// Card di un periodo (un mese) del report.
function ReportPeriodCard({ heading, monthName, periodStart, periodEnd, isCurrent, stats }) {
  return (
    <Card sx={{ borderRadius: 2, mb: 2 }}>
      <CardContent>
        <Typography
          fontSize={13}
          fontWeight={700}
          color="primary.main"
          sx={{ textTransform: "uppercase", letterSpacing: 0.3 }}
        >
          {heading}
        </Typography>
        <Typography fontSize={22} fontWeight={700} color="text.primary" mb={1}>
          {monthName}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
          <Chip
            size="small"
            icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
            label={`${fmtDayMonthYear(periodStart)} – ${fmtDayMonthYear(periodEnd)}`}
            sx={{ fontWeight: 600 }}
          />
          {isCurrent && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`Consegnate fino ad oggi (${fmtDayMonthYear(new Date())})`}
              sx={{ fontWeight: 600 }}
            />
          )}
        </Stack>
        <Stack gap={2}>
          <ReportMetricBlock
            icon={<EventAvailableIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
            title="Prenotazioni"
            approvedValue={stats.bookingsApproved}
            pendingValue={stats.bookingsPending}
          />
          <Divider />
          <ReportMetricBlock
            icon={<DirectionsBikeIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
            title="Bici consegnate"
            approvedValue={stats.bikesApproved}
            pendingValue={stats.bikesPending}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

// Pannello Report: mese corrente (solo tour già svolti, "consegnate fino ad
// oggi") e mese passato (intero). Usa TUTTE le prenotazioni, indipendentemente
// dalla sezione future/passate selezionata.
function ReportPanel({ allRequests }) {
  const now = new Date();

  // Mese corrente: dal primo giorno del mese fino ad ADESSO (tour già svolti).
  const curStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const curMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  const curStats = computePeriodStats(allRequests, curStart, now);

  // Mese passato: intero mese precedente (gestisce anche il cambio d'anno).
  const prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
  const prevEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  const prevStats = computePeriodStats(allRequests, prevStart, prevEnd);

  return (
    <Box px={2} py={2}>
      <Typography fontSize={22} fontWeight={700} color="text.primary" mb={0.5}>
        Report
      </Typography>
      <Typography fontSize={13} color="text.secondary" mb={2}>
        Prenotazioni e bici consegnate, per mese (le prenotazioni cancellate non
        sono conteggiate).
      </Typography>

      <ReportPeriodCard
        heading="Mese corrente"
        monthName={monthLabel(curStart)}
        periodStart={curStart}
        periodEnd={curMonthEnd}
        isCurrent
        stats={curStats}
      />
      <ReportPeriodCard
        heading="Mese passato"
        monthName={monthLabel(prevStart)}
        periodStart={prevStart}
        periodEnd={prevEnd}
        isCurrent={false}
        stats={prevStats}
      />
    </Box>
  );
}

export default function ManagerRequests() {
  const [tab, setTab] = useState(0);
  const [allRequests, setAllRequests] = useState([])
  // Stato di caricamento della chiamata bookings: mostriamo uno spinner finché
  // i dati non sono pronti, così l'utente capisce che sta caricando invece di
  // vedere un pannello vuoto ("nessuna richiesta") che sembrerebbe un errore.
  const [loading, setLoading] = useState(true)
  // Tracciamo se ci siamo già "auto-selezionati" una prenotazione tramite
  // ?bookingId in URL, così non riapriamo il dettaglio se l'utente torna
  // indietro alla lista.
  const [autoSelectedFor, setAutoSelectedFor] = useState(null);
  // Apertura della sidebar mobile (drawer temporaneo aperto dal burger menu).
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Il link "Gestisci giorni e orari di chiusura" dell'admin apre direttamente
  // la sezione degli orari (?sezione=orari).
  const [section, setSection] = useState(
    () => (searchParams.get("sezione") === "orari" ? "orari" : "future")
  )
  // Normalmente il parametro è ?bookingId=<id>. Alcuni messaggi WhatsApp già
  // inviati, per lo stesso refuso del template che ha prodotto '{{1}}', hanno il
  // parametro scritto come ?bookingld=<id> (con la 'l'): lo leggiamo come
  // fallback così anche quei link aprono comunque la prenotazione.
  const bookingIdParam = searchParams.get("bookingId") || searchParams.get("bookingld");

  // Token del pannello manager. Quando si apre il link da email/whatsapp
  // arriva nel path (/manager/:token): lo salviamo in localStorage e
  // ripuliamo l'URL (vedi effetto sotto) lasciando solo i query param
  // (es. ?bookingId). Nelle visite successive (/manager senza token) lo
  // recuperiamo da localStorage, così la pagina funziona comunque.
  const storedToken = (key) => {
    try { return localStorage.getItem(key) || undefined; } catch (e) { return undefined; }
  };
  const [token] = useState(() => {
    if (params.token) {
      // Il token sta subito dopo 'manager/'. In alcuni messaggi WhatsApp già
      // inviati, per un refuso del template, davanti al token è finito il
      // placeholder letterale '{{1}}' (/manager/{{1}}<token>). Togliamo SOLO
      // quel prefisso iniziale così la chiamata API parte pulita e non va in 400.
      const clean = params.token.startsWith("{{1}}")
        ? params.token.slice("{{1}}".length)
        : params.token;
      if (isScheduleToken(clean)) {
        // Token degli orari: serve solo alla sezione "Orari e chiusure". Il
        // token delle prenotazioni resta quello salvato (se c'è).
        try { localStorage.setItem(SCHEDULE_TOKEN_KEY, clean); } catch (e) { /* ignore */ }
        return storedToken(MANAGER_TOKEN_KEY);
      }
      try {
        localStorage.setItem(MANAGER_TOKEN_KEY, clean);
        // Un token delle prenotazioni vale anche per gli orari: via quello vecchio.
        localStorage.removeItem(SCHEDULE_TOKEN_KEY);
      } catch (e) { /* ignore */ }
      return clean;
    }
    return storedToken(MANAGER_TOKEN_KEY);
  });
  // Token per la sezione degli orari: quello degli orari se c'è, altrimenti
  // quello delle prenotazioni (il server accetta entrambi).
  const [scheduleToken] = useState(() => storedToken(SCHEDULE_TOKEN_KEY) || token);

  // Ripulisce l'URL togliendo il token dal path e mantenendo i query param.
  // Usiamo window.history.replaceState così non ricarichiamo la pagina e,
  // premendo "indietro", non si torna sull'URL che conteneva il token.
  useEffect(() => {
    if (!params.token) return;
    const search = window.location.search || "";
    window.history.replaceState(null, "", `/manager${search}`);
  }, [params.token]);

  const TABS = [
    { label: "DA APPROVARE", status: "pending" },
    { label: "Approvate",  status: "approved" },
    { label: "Cancellate",  status: "refused" },
  ];

  useEffect(() => {
    if (!token) {
      // Nessun token (né nel path né in localStorage): niente da caricare.
      setAllRequests([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    ManagerService.bookings(token).then((data) => {
      // L'endpoint ritorna sempre 200 con un array di prenotazioni; in caso di
      // errore (es. token non valido) potrebbe rispondere con { error: "..." }.
      // In quel caso NON facciamo fallback al mock: mostriamo lista vuota e
      // logghiamo, altrimenti l'id auto-select non matcherebbe mai.
      if (!Array.isArray(data)) {
        // Il backend risponde 403 con { error, customer_status } se il negozio
        // è "closed" o "free-expired": in quel caso redirect a /admin/ dove il
        // middleware di cortesia mostra la pagina giusta (rinnovo pagamento o
        // negozio chiuso). Usiamo replace() così "indietro" non torna qui.
        const blocked = data && (data.customer_status === "closed" || data.customer_status === "free-expired");
        if (blocked) {
          window.location.replace("/admin/");
          return;
        }
        console.warn("ManagerService.bookings: risposta non-array", data);
        setAllRequests([]);
        return;
      }
      setAllRequests(data.map(x => ({ ...x, tourDate: new Date(x.tourDate) })));
    }).catch((err) => {
      // Solo per fase di sviluppo: se la fetch crasha del tutto (es. backend
      // non raggiungibile) carichiamo dati mock per poter lavorare sulla UI.
      console.warn("ManagerService.bookings: fetch fallita, uso mock", err);
      setAllRequests(ALL_REQUESTS.map(x => ({ ...x, tourDate: new Date(x.tourDate) })));
    }).finally(() => {
      setLoading(false);
    });
  }, [])

  // Quando nell'URL c'è ?bookingId (link da email/whatsapp oppure apertura
  // del dettaglio dalla lista), appena le prenotazioni sono caricate
  // allineiamo sezione e tab a quella prenotazione. Il dettaglio in sé è
  // mostrato direttamente in base al bookingId presente nell'URL (vedi
  // selectedRequest), così "Indietro" del browser torna alla lista.
  // Lo facciamo una volta sola per id, per non forzare sezione/tab se
  // l'utente li cambia mentre il dettaglio è aperto.
  useEffect(() => {
    if (!bookingIdParam || allRequests.length === 0) return;
    if (autoSelectedFor === bookingIdParam) return;
    const target = allRequests.find(
      (r) => String(r.id) === String(bookingIdParam)
    );
    if (target) {
      // se la prenotazione è passata, switchiamo anche la sezione
      if (isExpired(target.tourDate)) setSection("passate");
      // NB: NON cambiamo più il tab in base allo status della prenotazione.
      // Tornando in lista l'utente deve sempre trovarsi sul tab "DA APPROVARE"
      // (tab 0), così vede subito se ha gestito tutte le richieste. Il
      // dettaglio della prenotazione resta comunque visibile perché è guidato
      // dal ?bookingId nell'URL, indipendentemente dal tab selezionato.
      setAutoSelectedFor(bookingIdParam);
    }
  }, [bookingIdParam, allRequests, autoSelectedFor]);

  const onSectionChange = (s) => {
    setSection(s)
    // Su mobile, scelta una sezione chiudiamo la sidebar temporanea.
    setMobileMenuOpen(false)
    // Se siamo nel dettaglio di una prenotazione, cliccare una voce del menu
    // a sinistra deve riportare alla lista sul tab scelto: togliamo il
    // bookingId dall'URL così il dettaglio si chiude.
    if (bookingIdParam) {
      navigate("/manager")
    }
  }

  const requests = allRequests.filter(t => (
    (isExpired(t.tourDate) && section === 'passate') ||
    (!isExpired(t.tourDate) && section === 'future'))
  )

  // Conteggi totali per i badge della sidebar
  const futureCount = allRequests.filter(t => !isExpired(t.tourDate)).length;
  const pastCount = allRequests.filter(t => isExpired(t.tourDate)).length;

  // Il dettaglio è guidato dall'URL: se c'è ?bookingId mostriamo la
  // prenotazione corrispondente, altrimenti la lista. Così l'apertura del
  // dettaglio è una vera navigazione e "Indietro" del browser torna in lista.
  const selectedRequest = bookingIdParam
    ? allRequests.find((r) => String(r.id) === String(bookingIdParam)) || null
    : null;

  // Apertura del dettaglio dalla lista: aggiunge una voce nello storico
  // del browser (push), così il tasto "Indietro" riporta alla lista.
  // Marchiamo lo state per sapere che siamo arrivati dalla lista in-app.
  const handleView = (r) => {
    navigate(`/manager?bookingId=${encodeURIComponent(r.id)}`, {
      state: { fromList: true },
    });
  };

  // Aggiorna in-place una singola prenotazione (usato dopo salvataggio altezze)
  const handleRequestUpdated = (updated) => {
    setAllRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  // Il tasto "Indietro" interno al dettaglio: se siamo arrivati dalla lista
  // in-app torniamo indietro nello storico (come il "Indietro" del browser);
  // se invece si è atterrati direttamente sul dettaglio (link da
  // email/whatsapp) andiamo alla lista, per non uscire dal sito.
  const handleBack = () => {
    if (location.state && location.state.fromList) {
      navigate(-1);
    } else {
      navigate("/manager");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div style={{maxWidth: "1024px"}}>
        <AppSidebar
          onChange={onSectionChange}
          activeSection={section}
          futureCount={futureCount}
          pastCount={pastCount}
          isMobile={isMobile}
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
        />
        <Box sx={{ backgroundColor: "background.default", minHeight: "100vh", p: 0, width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`, marginLeft: isMobile ? 0 : `${drawerWidth}px` }}>
        {section === "orari" ? (
          <>
            {/* Su mobile la sidebar è chiusa: serve il burger per cambiare sezione. */}
            {isMobile && (
              <Box sx={{ backgroundColor: "background.paper", borderBottom: "1px solid #E8E8E4" }}>
                <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: 1, pt: 1, pb: 0.5 }}>
                  <IconButton edge="start" onClick={() => setMobileMenuOpen(true)} aria-label="Apri menu">
                    <MenuIcon />
                  </IconButton>
                  <Typography fontSize={16} fontWeight={700} color="text.primary">
                    {SECTION_LABELS[section]}
                  </Typography>
                </Stack>
              </Box>
            )}
            <SchedulePanel token={scheduleToken} />
          </>
        ) : loading ? (
          <Stack alignItems="center" justifyContent="center" gap={2} sx={{ minHeight: "60vh", px: 2 }}>
            <CircularProgress color="primary" />
            <Typography fontSize={14} color="text.secondary">
              Caricamento prenotazioni…
            </Typography>
          </Stack>
        ) : selectedRequest ? (
          <RequestDetail
            request={selectedRequest}
            onBack={handleBack}
            token={token}
            onRequestUpdated={handleRequestUpdated}
          />
        ) : (
        <>
          {/* Header: burger (mobile) + tab. Nella sezione Report non mostriamo
              le tab (non ci sono liste da filtrare), ma su mobile teniamo la
              barra col burger così si può riaprire la sidebar e cambiare voce. */}
          {(section !== "report" || isMobile) && (
            <Box
              sx={{
                backgroundColor: "background.paper",
                borderBottom: "1px solid #E8E8E4",
                px: 2, pt: 2.5, pb: 0, padding: isMobile? 0: 'auto'
              }}
            >
              {/* Su mobile: burger menu (apre la sidebar) + nome sezione corrente.
                  Così la sidebar a sinistra sparisce e l'utente capisce sempre in
                  quale sezione si trova. */}
              {isMobile && (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  sx={{ px: 1, pt: 1, pb: 0.5 }}
                >
                  <IconButton
                    edge="start"
                    onClick={() => setMobileMenuOpen(true)}
                    aria-label="Apri menu"
                  >
                    <MenuIcon />
                  </IconButton>
                  <Typography fontSize={16} fontWeight={700} color="text.primary">
                    {SECTION_LABELS[section]}
                  </Typography>
                </Stack>
              )}
              {section !== "report" && (
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  textColor="primary"
                  indicatorColor="primary"
                  sx={{ minHeight: 44,
                    '& button': {
                      padding: '0px',
                      paddingLeft: '15px',
                    }
                  }}
                >
                  {TABS.map((t, i) => (
                    <Tab key={t.status} label={<TabLabel allRequests={requests} label={t.label} status={t.status}/>}/>
                  ))}
                </Tabs>
              )}
            </Box>
          )}

          {/* Panel */}
          {section === "report" ? (
            <ReportPanel allRequests={allRequests} />
          ) : (
            <Box px={2}>
              <CategoryPanel
                status={TABS[tab].status}
                allRequests={requests}
                onView={handleView}
                section={section}
              />
            </Box>
          )}
          </>
        )}
        </Box>
      </div>
    </ThemeProvider>
  );
}

export function AppSidebar({
  onChange,
  activeSection = 'future',
  futureCount = 0,
  pastCount = 0,
  isMobile = false,
  mobileOpen = false,
  onMobileClose = () => {},
}) {
  const navigate = useNavigate();

  const menuItems = [
    {
      text: 'Prenotazioni Future',
      icon: <EventAvailableIcon />,
      id: 'future',
      count: futureCount,
    },
    {
      text: 'Prenotazioni Passate',
      icon: <HistoryIcon />,
      id: 'passate',
      count: pastCount,
    },
    {
      text: 'Report',
      icon: <AssessmentOutlinedIcon />,
      id: 'report',
    },
    {
      text: 'Orari e chiusure',
      icon: <ScheduleOutlinedIcon />,
      id: 'orari',
    },
  ];

  // La sezione attiva è ora controllata dal componente padre (activeSection),
  // così resta in sync con il titolo mostrato sopra le tab e con gli switch
  // automatici di sezione (es. deep link a una prenotazione passata).
  const _setActiveTab = (status) => {
    onChange(status)
    if (isMobile) onMobileClose()
  }

  const CountBadge = ({ value, active }) => (
    <Box
      sx={{
        minWidth: 22,
        height: 20,
        borderRadius: "10px",
        backgroundColor: active ? "primary.main" : "action.selected",
        color: active ? "#fff" : "text.secondary",
        fontSize: 11,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 0.7,
      }}
    >
      {value}
    </Box>
  );

  return (
    <Drawer
      // Su mobile la sidebar diventa un drawer temporaneo aperto dal burger
      // menu (niente più mini-barra di icone a sinistra). Su desktop resta
      // permanente come prima.
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? mobileOpen : true}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        flexShrink: 0,
        width: isMobile ? 0 : drawerWidth,
        [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
      }}
    >
      <Stack direction="row" alignItems="center" padding={1} gap={1} sx={{cursor: 'pointer'}} onClick={() => {
        navigate("/manager")
      }}>
        <img
          src={`${process.env.PUBLIC_URL}/logo192.png`}
          style={{ width: 50, height: 50 }}
          alt="Outdoora"
        />
        <div>
          <Typography fontSize={17} fontWeight={700} color="text.primary">
          OUTDOORA
          </Typography>
          <Typography fontSize={15} color="text.secondary">
            Richieste di noleggio
          </Typography>
        </div>
      </Stack>
      <Divider/>
      <Box sx={{ overflow: 'auto' }}>
        <List>
          {menuItems.map((item) => {
            const active = activeSection === item.id;
            return (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={active}
                  onClick={() => _setActiveTab(item.id)}
                >
                  <ListItemIcon
                    sx={{
                      color: active ? 'primary.main' : 'inherit',
                      minWidth: 40,
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                  {typeof item.count === "number" && (
                    <CountBadge value={item.count} active={active} />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <Divider />
      </Box>
    </Drawer>
  );
}
